import {
  type QuizAdditionalCategory,
  type QuizQuestionChoice,
  type QuizQuestionChoiceOption,
} from '@efdigital/ll-addon-data';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { uniqueId } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { QuizQuestionScoringEditorComponentSignature } from '../../types/question-manager';
import { CategoryScores } from './shared/category-scores.gts';
import { ScoringWizard } from './shared/scoring-wizard.gts';

function not(value: boolean) {
  return !value;
}

export class SingleChoiceScoringEditorComponent extends Component<QuizQuestionScoringEditorComponentSignature> {
  get questionData(): QuizQuestionChoice {
    return this.args.question.choice!;
  }

  get isScoreIndividually() {
    return this.questionData.scoring === 'individual';
  }

  setCorrectAnswerCategoryScores = (
    score: number,
    category: string,
    additionalCategories: QuizAdditionalCategory[]
  ) => {
    this.questionData.score = score;
    this.questionData.category = category;
    this.questionData.additionalCategories = additionalCategories;
  };

  setOptionCategoryScores = (
    option: QuizQuestionChoiceOption,
    score: number,
    category: string,
    additionalCategories: QuizAdditionalCategory[]
  ) => {
    option.score = score;
    option.category = category;
    option.additionalCategories = additionalCategories;
  };

  setScoring = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.questionData.scoring = target.value;
  };

  setCorrectAnswer = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const correctOptionId = target.value;

    const choices = this.questionData.choices.map((option) => ({
      ...option,
      correct: option.oid === correctOptionId,
    }));

    this.questionData.choices = choices;
  };

  get correctAnswer(): QuizQuestionChoiceOption | null {
    const option = this.questionData.choices.find((option) => option.correct);
    if (!option?.oid) {
      return null;
    }
    return option;
  }

  get hasAnyScoring() {
    if (this.isScoreIndividually) {
      return this.questionData.choices.some((option) => option.score);
    }
    return true;
  }

  @tracked forceShowScoring = false;

  showScoring = () => {
    this.forceShowScoring = true;
  };

  get isScoringEnabled() {
    return this.forceShowScoring || this.hasAnyScoring;
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="card-body">

        <div class="form-label">If the user provides an answer, they will be
          scored as follows:</div>

        <div class="mb-4">
          <div class="checkbox">
            <label>
              <input
                id="{{uniqueId}}-scoring-individual"
                type="radio"
                value="individual"
                checked={{this.isScoreIndividually}}
                {{on "change" this.setScoring}}
                class="form-check-input"
              />
              Each option has a different score</label>
          </div>

          <div class="checkbox">
            <label>
              <input
                id="{{uniqueId}}-scoring-exact"
                type="radio"
                value="exact"
                checked={{not this.isScoreIndividually}}
                {{on "change" this.setScoring}}
                class="form-check-input"
              />
              There is a single correct answer
            </label>
          </div>
        </div>
        {{#if this.isScoringEnabled}}
          {{#if this.isScoreIndividually}}
            <div class="mb-3">
              <ScoringWizard
                @questionData={{this.questionData}}
                @quiz={{@quiz}}
              />
            </div>

            {{#each this.questionData.choices as |option|}}
              <h5 class="mt-1 mb-2">{{option.value}}</h5>
              <CategoryScores
                @quiz={{@quiz}}
                @category={{option.category}}
                @score={{option.score}}
                @additionalCategories={{option.additionalCategories}}
                @onChange={{fn this.setOptionCategoryScores option}}
              />
            {{/each}}
          {{else}}
            {{#let this.correctAnswer as |correctAnswer|}}
              <div class="mb-3">
                <label for="{{uniqueId}}-correct-answer">The correct answer is:</label>
                <select
                  id="{{uniqueId}}-correct-answer"
                  class="form-select"
                  {{selectValue this.correctAnswer.oid}}
                  {{on "change" this.setCorrectAnswer}}
                >
                  {{#each this.questionData.choices as |choice|}}
                    <option value={{choice.oid}}>{{choice.value}}</option>
                  {{/each}}
                </select>
              </div>

              {{#if correctAnswer}}
                <CategoryScores
                  @quiz={{@quiz}}
                  @category={{this.questionData.category}}
                  @score={{this.questionData.score}}
                  @additionalCategories={{this.questionData.additionalCategories}}
                  @onChange={{this.setCorrectAnswerCategoryScores}}
                />
              {{/if}}
            {{/let}}
          {{/if}}
        {{else}}
          <div class="mb-3 text-center">
            <button
              type="button"
              class="btn btn-primary"
              {{on "click" this.showScoring}}
            >
              Configure scoring
            </button>
          </div>
        {{/if}}
      </div>
    {{/let}}
  </template>
}
